import { IReactFlowData } from '@query'
import Color from 'color'
import React from 'react'
import styled, { useTheme } from 'styled-components'
import { getNodeDefinition } from '../nodes/registry'

interface Props {
  data: IReactFlowData
}

export const VIEW_WIDTH = 240
export const VIEW_HEIGHT = 96
const PADDING = 14
const FALLBACK_NODE_WIDTH = 260
const FALLBACK_NODE_HEIGHT = 90

export const WorkflowThumbnail: React.FC<Props> = ({ data }) => {
  const theme = useTheme()
  const nodes = data?.nodes || []
  const edges = data?.edges || []

  if (nodes.length === 0) {
    return <svg width="100%" height={VIEW_HEIGHT} viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`} />
  }

  const boxes = nodes.map(node => ({
    id: node.id,
    x: node.position.x,
    y: node.position.y,
    width: node.measured?.width || FALLBACK_NODE_WIDTH,
    height: node.measured?.height || FALLBACK_NODE_HEIGHT,
    accent: getNodeDefinition(node.type).accentColor(theme)
  }))

  const minX = Math.min(...boxes.map(box => box.x))
  const minY = Math.min(...boxes.map(box => box.y))
  const maxX = Math.max(...boxes.map(box => box.x + box.width))
  const maxY = Math.max(...boxes.map(box => box.y + box.height))

  const scale = Math.min(
    (VIEW_WIDTH - PADDING * 2) / Math.max(maxX - minX, 1),
    (VIEW_HEIGHT - PADDING * 2) / Math.max(maxY - minY, 1)
  )

  const contentWidth = (maxX - minX) * scale
  const contentHeight = (maxY - minY) * scale
  const offsetX = (VIEW_WIDTH - contentWidth) / 2
  const offsetY = (VIEW_HEIGHT - contentHeight) / 2

  const project = (x: number, y: number): [number, number] => [
    offsetX + (x - minX) * scale,
    offsetY + (y - minY) * scale
  ]

  return (
    <svg width="100%" height={VIEW_HEIGHT} viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}>
      {edges.map(edge => {
        const source = boxes.find(box => box.id === edge.source)
        const target = boxes.find(box => box.id === edge.target)
        if (!source || !target) return null

        const [x1, y1] = project(source.x + source.width, source.y + source.height / 2)
        const [x2, y2] = project(target.x, target.y + target.height / 2)
        const bend = Math.max((x2 - x1) / 2, 16)

        return (
          <path
            key={edge.id}
            d={`M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`}
            fill="none"
            stroke={theme.colors.iconGray}
            strokeWidth={1.4}
            strokeLinecap="round"
            opacity={0.7}
          />
        )
      })}

      {boxes.map(box => {
        const [x, y] = project(box.x, box.y)
        const width = box.width * scale
        const height = box.height * scale

        return (
          <g key={box.id}>
            <rect
              x={x}
              y={y}
              width={width}
              height={height}
              rx={3}
              fill={theme.colors.neutralWhite}
              stroke={theme.colors.neutralGray}
              strokeWidth={1}
            />
            <rect
              x={x}
              y={y}
              width={width}
              height={Math.min(5, height / 3)}
              rx={2}
              fill={box.accent}
            />
          </g>
        )
      })}
    </svg>
  )
}

/* Mirrors the builder canvas: same dot grid, gap, and dot opacity. */
export const ThumbnailWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.defaultHover};
  background-image: radial-gradient(
    ${({ theme }) => Color(theme.colors.iconGray).alpha(0.35).string()} 0.7px,
    transparent 0.7px
  );
  background-size: 22px 22px;
  background-position: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`
